import type {
  GetApiV1BeachAbvParams,
  GetApiV1BeachFolderTournamentsParams,
  GetApiV1BeachLeaderboardLeaderboardIdPlayersParams,
  GetApiV1BeachLeaderboardListParams,
  GetApiV1BeachLeaderboardSettingsParams,
  GetApiV1BeachMyTournamentsCanSignUpParams,
  GetApiV1BeachMyTournamentsFutureParams,
  GetApiV1BeachPlayerUserIdLeaderboardLeaderboardIdParams,
  GetApiV1BeachRefereeListListIdParams,
  GetApiV1BeachRefereeListParams,
  GetApiV1BeachRefereeMyDelegationParams,
  GetApiV1BeachRefereeMyDelegationUnavailabilityParams,
  GetApiV1BeachRefereeTournamentListParams,
  GetApiV1BeachRefereeTournamentTournamentIdRefereeParams,
  GetApiV1BeachRefereeUserIdDelegationParams,
  GetApiV1BeachRefereeUserIdUnavailabilityParams,
  GetApiV1BeachStatisticParams,
  GetApiV1BeachStatisticsRefereeDelegationsParams,
  GetApiV1BeachStatisticsRefereeEvaluationParams,
  GetApiV1BeachStatisticsRefereeUnavailabilityParams,
  GetApiV1BeachTemplatesEvaluationsParams,
  GetApiV1BeachTemplatesSchedulesParams,
  GetApiV1BeachTournamentTournamentIdDetailParams,
  GetApiV1ClubAbvParams,
  GetApiV1RequestFormBeachTournamentParams,
} from '@1webit/cvs-api-scheme/models';
import type {
  GetApiV1BeachRefereeDelegationListListIdParams,
} from '@1webit/cvs-api-scheme/models/getApiV1BeachRefereeDelegationListListIdParams';
import type {
  GetApiV1BeachRefereePaymentsTournamentsParams,
} from '@1webit/cvs-api-scheme/models/getApiV1BeachRefereePaymentsTournamentsParams';
import { queryOptions } from '@tanstack/react-query';

import { DEFAULT_TABLE_PAGE_SIZE, MAX_API_LIMIT } from '@/constants';
import type { BaseTableParams } from '@/utils';
import { getResponse, paginationToApiFormat, queryParams } from '@/utils';

export enum BeachClubTypes {
  ALL = 'all',
  BEACH = 'beach',
  ABV = 'abv',
  COUNTY = 'county',
}

export const getApiV1BeachMyTournamentsFuture = (page = 1, limit = DEFAULT_TABLE_PAGE_SIZE, params?: Omit<GetApiV1BeachMyTournamentsFutureParams, 'offset' | 'limit'>) => queryOptions({
  queryKey: ['getApiV1BeachMyTournamentsFuture', page, limit, params],
  queryFn: async () => {
    const { responseData, ...res } = await getResponse('getApiV1BeachMyTournamentsFuture', {
      ...params,
      limit,
      offset: (page - 1) * limit,
    });
    return {
      ...res,
      responseData: responseData.map(item => ({
        ...item,
        id: item.tournamentId,
      })),
    };
  },
});

export const getApiV1BeachMyTournamentsCanSignUp = (page = 1, limit = DEFAULT_TABLE_PAGE_SIZE, params?: Omit<GetApiV1BeachMyTournamentsCanSignUpParams, 'offset' | 'limit'>) => {
  return queryOptions({
    queryKey: ['getApiV1BeachMyTournamentsCanSignUp', page, limit, params],
    queryFn: () => getResponse('getApiV1BeachMyTournamentsCanSignUp', queryParams({
      ...params,
      limit,
      offset: (page - 1) * limit,
    })),
  });
};

export const getApiV1BeachMyTournamentsPlayed = (page = 1, limit = DEFAULT_TABLE_PAGE_SIZE, sort?: string) => {
  return queryOptions({
    queryKey: ['getApiV1BeachMyTournamentsPlayed', page, limit, sort],
    queryFn: async () => {
      const res = await getResponse('getApiV1BeachMyTournamentsPlayed', queryParams({
        offset: (page - 1) * limit,
        limit,
        sort,
      }));
      return {
        ...res,
        responseData: res.responseData.map(item => ({
          ...item,
          id: item.tournamentId,
        })),
      };
    },
  });
};

/**
 * Gets beach folder tournaments
 */
export const getApiV1BeachFolderTournaments = (params: BaseTableParams<GetApiV1BeachFolderTournamentsParams>) => queryOptions({
  queryKey: ['getApiV1BeachFolderTournaments', params],
  queryFn: () => getResponse('getApiV1BeachFolderTournaments', paginationToApiFormat(params, true)),
});

// tournaments
export const getApiV1BeachTournament = (year?: number, category?: string, folderId?: number, page = 1, limit = DEFAULT_TABLE_PAGE_SIZE, sort = 'tournamentStartDate:desc') => {
  let search: undefined | string;
  if (category !== undefined || folderId !== undefined || year !== undefined) {
    search = [
      category ? `category:${category}` : false,
      folderId !== undefined ? `folderId:${folderId}` : false,
      year ? `year:${year}` : false,
    ].filter(Boolean).join(';');
  }

  return queryOptions({
    queryKey: ['getApiV1BeachTournament', page, limit, sort, search, year],
    queryFn: () => getResponse('getApiV1BeachTournament', queryParams({
      offset: (page - 1) * limit,
      limit,
      sort,
      search,
    })),
  });
};

// category
export const getApiV1BeachCategory = queryOptions({
  queryKey: ['getApiV1BeachCategory'],
  queryFn: () => getResponse('getApiV1BeachCategory'),
});

// folder
export const getApiV1BeachFolder = (page = 1, sort?: string, limit = 100, mainFolder?: boolean) => {
  return queryOptions({
    queryKey: ['getApiV1BeachFolder', page, sort, limit, mainFolder],
    queryFn: () => getResponse('getApiV1BeachFolder', queryParams({
      mainFolder,
      offset: (page - 1) * limit,
      sort,
      limit,
    })),
  });
};

/**
 * Gets beach subfolders
 * @param folderId - Folder ID (root folder containing all folders has ID 1)
 */
export const getApiV1BeachFolderFolderIdSubfolders = (folderId: number) => queryOptions({
  queryKey: ['getApiV1BeachFolderFolderIdSubfolders', folderId],
  queryFn: () => getResponse('getApiV1BeachFolderFolderIdSubfolders', folderId),
});

export const getApiV1BeachLeaderboardLeaderboardId = (leaderboardId: string) =>
  queryOptions({
    queryKey: ['getApiV1BeachLeaderboardLeaderboardId', leaderboardId],
    queryFn: () =>
      getResponse('getApiV1BeachLeaderboardLeaderboardId', leaderboardId),
  });

/**
 * Gets beach leaderboard list
 * @param page
 * @param params
 */
export const getApiV1BeachLeaderboardList = (page = 1, params?: Omit<GetApiV1BeachLeaderboardListParams, 'offset'>) => queryOptions({
  queryKey: ['getApiV1BeachLeaderboardList', page, params],
  queryFn: () => getResponse('getApiV1BeachLeaderboardList', {
    ...params,
    limit: params?.limit ?? MAX_API_LIMIT,
    offset: (page - 1) * (params?.limit ?? MAX_API_LIMIT),
  }),
});

/**
 * Gets beach tournament detail
 * @param id - tournament ID
 */
export const getApiV1BeachTournamentTournamentIdDetail = (id: number, params?: GetApiV1BeachTournamentTournamentIdDetailParams) => queryOptions({
  queryKey: ['getApiV1BeachTournamentTournamentIdDetail', id, params],
  queryFn: () => getResponse('getApiV1BeachTournamentTournamentIdDetail', id, params),
});

/**
 * Gets beach tournament attachment
 * @param id - tournamentId
 * @param params - query parameters
 */
export const getApiV1BeachTournamentTournamentIdAttachment = (id: number, page = 1, limit = DEFAULT_TABLE_PAGE_SIZE, sort?: string) => queryOptions({
  queryKey: ['getApiV1BeachTournamentTournamentIdAttachment', id, page, limit, sort],
  queryFn: () => getResponse('getApiV1BeachTournamentTournamentIdAttachment', id, queryParams({
    offset: (page - 1) * limit,
    limit,
    sort,
  })),
});

/**
 * Gets beach tournament main competition matches
 * @param id - tournament id
 * @param page
 * @param sort
 * @param params
 */
export const getApiV1BeachTournamentTournamentIdMainCompetitionMatches = (id: number, page = 1, limit = DEFAULT_TABLE_PAGE_SIZE, sort?: string) => queryOptions({
  queryKey: ['getApiV1BeachTournamentTournamentIdMainCompetitionMatches', id, page, limit, sort],
  queryFn: () => getResponse('getApiV1BeachTournamentTournamentIdMainCompetitionMatches', id, queryParams({
    offset: (page - 1) * limit,
    limit,
    sort,
  })),
});

/**
 * Gets beach tournament starting fee
 * @param id - tournament id
 * @param page
 * @param params
 */
export const getApiV1BeachTournamentTournamentIdStartingFee = (id: number, page = 1, limit = DEFAULT_TABLE_PAGE_SIZE, sort?: string) => queryOptions({
  queryKey: ['getApiV1BeachTournamentTournamentIdStartingFee', id, page, limit, sort],
  queryFn: () => getResponse('getApiV1BeachTournamentTournamentIdStartingFee', id, queryParams({
    offset: (page - 1) * limit,
    limit,
    sort,
  })),
});

/**
 * Gets beach statistic
 * @param page
 * @param params
 */
export const getApiV1BeachStatistic = (page = 1, params?: GetApiV1BeachStatisticParams) => queryOptions({
  queryKey: ['getApiV1BeachStatistic', page, params],
  queryFn: () => getResponse('getApiV1BeachStatistic', {
    ...params,
    offset: (page - 1) * DEFAULT_TABLE_PAGE_SIZE,
    limit: DEFAULT_TABLE_PAGE_SIZE,
  }),
});

/**
 * Gets beach tournament main competition script
 */
export const getApiV1BeachTournamentMainCompetitionScript = (tournamentId?: number) =>
  queryOptions({
    queryKey: ['getApiV1BeachTournamentMainCompetitionScript', tournamentId],
    queryFn: () =>
      getResponse('getApiV1BeachTournamentMainCompetitionScript', {
        tournamentId,
      }),
  });

/**
 * Gets beach leaderboard players
 * @param leaderboardId
 * @param page
 * @param params
 */
export const getApiV1BeachLeaderboardLeaderboardIdPlayers = (leaderboardId: string, page = 1, limit = DEFAULT_TABLE_PAGE_SIZE, params?: GetApiV1BeachLeaderboardLeaderboardIdPlayersParams) => queryOptions({
  queryKey: ['getApiV1BeachLeaderboardLeaderboardIdPlayers', leaderboardId, page, limit, params],
  queryFn: () => getResponse('getApiV1BeachLeaderboardLeaderboardIdPlayers', leaderboardId, queryParams({
    ...params,
    offset: (page - 1) * limit,
    limit,
  })),
});

/**
 * Gets beach referee past matches
 * @param page
 * @param limit
 * @param params
 */
export const getApiV1BeachRefereeMyDelegation = (page = 1, limit = DEFAULT_TABLE_PAGE_SIZE, params?: GetApiV1BeachRefereeMyDelegationParams) => queryOptions({
  queryKey: ['getApiV1BeachRefereeMyDelegation', page, limit, params],
  queryFn: () => getResponse('getApiV1BeachRefereeMyDelegation', {
    ...params,
    offset: (page - 1) * limit,
    limit,
  }),
});

/**
 * Gets beach referee unavailability
 * @param page
 * @param params
 */
export const getApiV1BeachRefereeMyDelegationUnavailability = (page = 1, limit = DEFAULT_TABLE_PAGE_SIZE, params?: GetApiV1BeachRefereeMyDelegationUnavailabilityParams) => queryOptions({
  queryKey: ['getApiV1BeachRefereeMyDelegationUnavailability', page, limit, params],
  queryFn: () => getResponse('getApiV1BeachRefereeMyDelegationUnavailability', {
    ...params,
    offset: (page - 1) * limit,
    limit,
  }),
});

/**
 * Gets beach tournament main competition draw
 * @param id - tournament id
 * @param page
 * @param params
 */
export const getApiV1BeachTournamentTournamentIdResult = (id: number, page = 1, limit = DEFAULT_TABLE_PAGE_SIZE, sort?: string) => queryOptions({
  queryKey: ['getApiV1BeachTournamentTournamentIdResult', id, page, limit, sort],
  queryFn: async () => getResponse('getApiV1BeachTournamentTournamentIdResult', id, queryParams({
    offset: (page - 1) * limit,
    limit,
    sort,
  })),
});

/**
 * Gets beach templates evaluations
 * @param page
 * @param params
 */
export const getApiV1BeachTemplatesEvaluations = (page = 1, limit = DEFAULT_TABLE_PAGE_SIZE, params?: GetApiV1BeachTemplatesEvaluationsParams) => queryOptions({
  queryKey: ['getApiV1BeachTemplatesEvaluations', page, limit, params],
  queryFn: () => getResponse('getApiV1BeachTemplatesEvaluations', queryParams({
    ...params,
    offset: (page - 1) * limit,
    limit,
  })),
});

/**
 * Gets beach templates schedule
 * @param page
 * @param params
 */
export const getApiV1BeachTemplatesSchedules = (page = 1, limit = DEFAULT_TABLE_PAGE_SIZE, params?: GetApiV1BeachTemplatesSchedulesParams) => queryOptions({
  queryKey: ['getApiV1BeachTemplatesSchedules', page, limit, params],
  queryFn: () => getResponse('getApiV1BeachTemplatesSchedules', queryParams({
    ...params,
    offset: (page - 1) * limit,
    limit,
  })),
});

/**
 * Gets beach templates evaluation data
 * @param fileId
 */
export const getApiV1BeachTemplatesEvaluationFileId = (fileId: number) => queryOptions({
  queryKey: ['getApiV1BeachTemplatesEvaluationFileId', fileId],
  queryFn: async () => getResponse('getApiV1BeachTemplatesEvaluationFileId', fileId),
});

/**
 * Gets beach templates schedule data
 * @param fileId
 */
export const getApiV1BeachTemplatesScheduleFileId = (fileId: number) => queryOptions({
  queryKey: ['getApiV1BeachTemplatesScheduleFileId', fileId],
  queryFn: () => getResponse('getApiV1BeachTemplatesScheduleFileId', fileId),
});

/**
 * Gets beach referee tournament list
 * @param page
 * @param params
 */
export const getApiV1BeachRefereeTournamentList = (page = 1, limit = DEFAULT_TABLE_PAGE_SIZE, params?: GetApiV1BeachRefereeTournamentListParams) => queryOptions({
  queryKey: ['getApiV1BeachRefereeTournamentList', page, limit, params],
  queryFn: () => getResponse('getApiV1BeachRefereeTournamentList', {
    ...params,
    offset: (page - 1) * limit,
    limit,
  }),
});

/**
 * Gets beach referee list
 * @param page
 * @param params
 */
export const getApiV1BeachRefereeList = (page = 1, limit = DEFAULT_TABLE_PAGE_SIZE, params?: GetApiV1BeachRefereeListParams) => queryOptions({
  queryKey: ['getApiV1BeachRefereeList', page, limit, params],
  queryFn: () => getResponse('getApiV1BeachRefereeList', {
    ...params,
    offset: (page - 1) * limit,
    limit,
  }),
});

export const getApiV1BeachRefereeDelegationListListId = (listId: number, params: GetApiV1BeachRefereeDelegationListListIdParams) => queryOptions({
  queryKey: ['getApiV1BeachRefereeDelegationListListId', listId, params],
  queryFn: () => getResponse('getApiV1BeachRefereeDelegationListListId', listId, params),
});

/**
 * Gets beach referee list
 * @param id - list ID
 * @param page
 * @param params
 */
export const getApiV1BeachRefereeListListId = (id: number, page = 1, limit = DEFAULT_TABLE_PAGE_SIZE, params?: GetApiV1BeachRefereeListListIdParams) => queryOptions({
  queryKey: ['getApiV1BeachRefereeListListId', id, page, limit, params],
  queryFn: () => getResponse('getApiV1BeachRefereeListListId', id, {
    ...params,
    offset: (page - 1) * limit,
    limit,
  }),
});

/**
 * Gets beach referee tournament detail
 * @param id - tournament id
 */
export const getApiV1BeachRefereeTournamentTournamentId = (id: number) => queryOptions({
  queryKey: ['getApiV1BeachRefereeTournamentTournamentId', id],
  queryFn: () => getResponse('getApiV1BeachRefereeTournamentTournamentId', id),
});

/**
 * Gets beach referee tournament referee list
 * @param id - tournament id
 * @param page
 * @param params
 */
export const getApiV1BeachRefereeTournamentTournamentIdReferee = (id: number, page = 1, limit = DEFAULT_TABLE_PAGE_SIZE, params?: GetApiV1BeachRefereeTournamentTournamentIdRefereeParams) => queryOptions({
  queryKey: ['getApiV1BeachRefereeTournamentTournamentIdReferee', id, page, limit, params],
  queryFn: () => getResponse('getApiV1BeachRefereeTournamentTournamentIdReferee', id, queryParams({
    ...params,
    offset: (page - 1) * limit,
    limit,
  })),
});

/**
 * Gets beach leaderboard settings
 * @param page
 * @param params
 */
export const getApiV1BeachLeaderboardSettings = (page = 1, params?: GetApiV1BeachLeaderboardSettingsParams) => queryOptions({
  queryKey: ['getApiV1BeachLeaderboardSettings', page, params],
  queryFn: () => getResponse('getApiV1BeachLeaderboardSettings', queryParams({
    offset: (page - 1) * DEFAULT_TABLE_PAGE_SIZE,
    limit: DEFAULT_TABLE_PAGE_SIZE,
    ...params,
  })),
});

/**
 * Gets beach referee user delegation
 * @param id - user ID
 * @param page
 * @param params
 */
export const getApiV1BeachRefereeUserIdDelegation = (id: number, page = 1, limit = DEFAULT_TABLE_PAGE_SIZE, params?: GetApiV1BeachRefereeUserIdDelegationParams) => queryOptions({
  queryKey: ['getApiV1BeachRefereeUserIdDelegation', id, page, limit, params],
  queryFn: () => getResponse('getApiV1BeachRefereeUserIdDelegation', id, {
    ...params,
    offset: (page - 1) * limit,
    limit,
  }),
});

/**
 * Gets beach referee user unavailability
 * @param userId
 * @param page
 * @param params
 */
export const getApiV1BeachRefereeUserIdUnavailability = (userId: number, page = 1, limit = DEFAULT_TABLE_PAGE_SIZE, params?: GetApiV1BeachRefereeUserIdUnavailabilityParams) => queryOptions({
  queryKey: ['getApiV1BeachRefereeUserIdUnavailability', userId, page, limit, params],
  queryFn: () => getResponse('getApiV1BeachRefereeUserIdUnavailability', userId, {
    ...params,
    offset: (page - 1) * limit,
    limit,
  }),
});

export const getApiV1BeachDelegationCategoryCategory = (key: string, folderId: number | undefined = undefined, page = 1, sort?: string, limit = 20) => {
  return queryOptions({
    queryKey: ['getApiV1BeachDelegationCategoryCategory', key, page, sort, limit, folderId],
    queryFn: () => getResponse('getApiV1BeachDelegationCategoryCategory', key, queryParams({
      offset: (page - 1) * limit,
      sort,
      limit,
      folderId,
    })),
  });
};

export const getApiV1BeachDelegationTournamentTournamentIdReferee = (id: number, page = 1, limit = 20) => {
  return queryOptions({
    queryKey: ['getApiV1BeachDelegationTournamentTournamentIdReferee', id, page, limit],
    queryFn: () => getResponse('getApiV1BeachDelegationTournamentTournamentIdReferee', id, queryParams({
      offset: (page - 1) * limit,
      limit,
    })),
  });
};

export const getApiV1DelegationTechnicalUserIdMatchList = (id: number, page = 1, limit = 20, sort?: string) => {
  return queryOptions({
    queryKey: ['getApiV1DelegationTechnicalUserIdMatchList', id, page, limit, sort],
    queryFn: () => getResponse('getApiV1DelegationTechnicalUserIdMatchList', id, queryParams({
      offset: (page - 1) * limit,
      limit,
      sort,
    })),
  });
};

export const getApiV1BeachDelegationReferee = (page = 1, limit = 20, sort?: string) => {
  return queryOptions({
    queryKey: ['getApiV1BeachDelegationReferee', page, limit, sort],
    queryFn: () => getResponse('getApiV1BeachDelegationReferee', queryParams({
      offset: (page - 1) * limit,
      limit,
      sort,
    })),
  });
};

export const getApiV1BeachDelegationRefereeUserIdTournaments = (id: number, page = 1, limit = 20, sort?: string) => {
  return queryOptions({
    queryKey: ['getApiV1BeachDelegationRefereeUserIdTournaments', id, page, limit, sort],
    queryFn: () => getResponse('getApiV1BeachDelegationRefereeUserIdTournaments', id, queryParams({
      offset: (page - 1) * limit,
      limit,
      sort,
    })),
  });
};
export const getApiV1BeachDelegationRefereeUserIdValuation = (id: number) => {
  return queryOptions({
    queryKey: ['getApiV1BeachDelegationRefereeUserIdValuation', id],
    queryFn: () => getResponse('getApiV1BeachDelegationRefereeUserIdValuation', id),
  });
};

export const getApiV1BeachStatisticsClubsType = (type: BeachClubTypes, page = 1, limit = DEFAULT_TABLE_PAGE_SIZE, params?: GetApiV1BeachTemplatesEvaluationsParams) => queryOptions({
  queryKey: ['getApiV1BeachStatisticsClubsType', type, page, limit, params],
  queryFn: () => getResponse('getApiV1BeachStatisticsClubsType', type, queryParams({
    ...params,
    offset: (page - 1) * limit,
    limit,
  })),
});
export const getApiV1BeachStatisticsSummary = () => queryOptions({
  queryKey: ['getApiV1BeachStatisticsSummary'],
  queryFn: () => getResponse('getApiV1BeachStatisticsSummary'),
});

export const getApiV1BeachStatisticsPlayers = (page = 1, limit = DEFAULT_TABLE_PAGE_SIZE, params?: GetApiV1BeachLeaderboardSettingsParams) => queryOptions({
  queryKey: ['getApiV1BeachStatisticsPlayers', page, limit, params],
  queryFn: () => getResponse('getApiV1BeachStatisticsPlayers', queryParams({
    ...params,
    offset: (page - 1) * limit,
    limit,
  })),
});

/**
 * Gets beach player user id leaderboards
 * @param userId
 * @param leaderboardId
 */
export const getApiV1BeachPlayerUserIdLeaderboards = (userId: number, leaderboardId?: string) => queryOptions({
  queryKey: ['getApiV1BeachPlayerUserIdLeaderboards', userId, leaderboardId ?? null],
  queryFn: () => getResponse('getApiV1BeachPlayerUserIdLeaderboards', userId, leaderboardId ? { leaderboardId } : undefined),
});

/**
 * Gets beach player leaderboard
 * @param userId
 * @param leaderboardId
 * @param params
 */
export const getApiV1BeachPlayerUserIdLeaderboardLeaderboardId = (userId: number, leaderboardId: string, page = 1, limit = DEFAULT_TABLE_PAGE_SIZE, params?: GetApiV1BeachPlayerUserIdLeaderboardLeaderboardIdParams) => queryOptions({
  queryKey: ['getApiV1BeachPlayerUserIdLeaderboardLeaderboardId', userId, leaderboardId, page, limit, params],
  queryFn: () => getResponse('getApiV1BeachPlayerUserIdLeaderboardLeaderboardId', userId, leaderboardId, queryParams({
    ...params,
    offset: (page - 1) * limit,
    limit,
  })),
});

/**
 * Gets beach leaderboards
 * @param page
 * @param params
 */
export const getApiV1BeachLeaderboards = (folderId?: number, page = 1, limit = DEFAULT_TABLE_PAGE_SIZE, sort?: string, search?: string) => {
  const params = queryParams({
    folderId,
    search,
    sort,
    offset: (page - 1) * limit,
    limit,
  });
  return queryOptions({
    queryKey: ['getApiV1BeachLeaderboards', params],
    queryFn: () => getResponse('getApiV1BeachLeaderboards', params),
  });
};

/**
 * Gets beach tournament qualification matches
 * @param id - tournament id
 * @param page
 * @param params
 */
export const getApiV1BeachTournamentTournamentIdQualificationMatches = (id: number, page = 1, limit = DEFAULT_TABLE_PAGE_SIZE, sort?: string) => queryOptions({
  queryKey: ['getApiV1BeachTournamentTournamentIdQualificationMatches', id, page, limit, sort],
  queryFn: () => getResponse('getApiV1BeachTournamentTournamentIdQualificationMatches', id, queryParams({
    offset: (page - 1) * limit,
    limit,
    sort,
  })),
});

/**
 * Gets beach qualification scripts
 */
export const getApiV1BeachTournamentQualificationScript = (tournamentId?: number) => queryOptions({
  queryKey: ['getApiV1BeachTournamentQualificationScript', tournamentId],
  queryFn: () => getResponse('getApiV1BeachTournamentQualificationScript', { tournamentId }),
});

/**
 * Gets beach tournament qualification match
 * @param matchId
 */
export const getApiV1BeachTournamentMatchIdQualificationMatch = (matchId: number) => queryOptions({
  queryKey: ['getApiV1BeachTournamentMatchIdQualificationMatch', matchId],
  queryFn: () => getResponse('getApiV1BeachTournamentMatchIdQualificationMatch', matchId),
});

/**
 * Gets beach tournament registration detail
 * @param registrationId
 */
export const getApiV1BeachTournamentRegistrationRegistrationIdDetail = (registrationId: number) => queryOptions({
  queryKey: ['getApiV1BeachTournamentRegistrationRegistrationIdDetail', registrationId],
  queryFn: () => getResponse('getApiV1BeachTournamentRegistrationRegistrationIdDetail', registrationId),
});

export const getApiV1BeachUserRights = ({
  folderId,
  tournamentId,
  leaderboardId,
}: {
  folderId?: number;
  tournamentId?: number;
  leaderboardId?: string;
}) => {
  return queryOptions({
    queryKey: ['getApiV1BeachUserRights', folderId, tournamentId, leaderboardId],
    queryFn: () => getResponse('getApiV1BeachUserRights', queryParams({ folderId, tournamentId, leaderboardId }),
    ),
  });
};

/**
 * Gets beach evaluation templates
 */
export const getApiV1BeachTemplatesEvaluation = (tournamentId?: number) =>
  queryOptions({
    queryKey: ['getApiV1BeachTemplatesEvaluation', tournamentId],
    queryFn: () =>
      getResponse('getApiV1BeachTemplatesEvaluation', { tournamentId }),
  });

/**
 * Gets beach evaluation templates values
 * @param fileId
 * @param tournamentId
 */
export const getApiV1BeachTemplatesEvaluationValuesFileId = (fileId: number, tournamentId: number) => queryOptions({
  queryKey: ['getApiV1BeachTemplatesEvaluationValuesFileId', fileId, tournamentId],
  queryFn: () => getResponse('getApiV1BeachTemplatesEvaluationValuesFileId', fileId, { tournamentId }),
});

export const getApiV1BeachTournamentDetailLists = queryOptions({
  queryKey: ['getApiV1BeachTournamentDetailLists'],
  queryFn: () => getResponse('getApiV1BeachTournamentDetailLists'),
});

export const getApiV1BeachAbv = (params?: GetApiV1BeachAbvParams) => queryOptions({
  queryKey: ['getApiV1ClubMember', params],
  queryFn: () => getResponse('getApiV1BeachAbv', queryParams(
    {
      ...params,
    },
  )),
});

export const getApiV1RequestFormBeachTournament = (params: GetApiV1RequestFormBeachTournamentParams) => queryOptions({
  queryKey: ['getApiV1RequestFormBeachTournament', params],
  queryFn: () => getResponse('getApiV1RequestFormBeachTournament', params),
});

export const getApiV1BeachTournamentTournamentIdAttachmentAttachmentId = (tournamentId: number, attachmentId: number) => queryOptions({
  queryKey: ['getApiV1BeachTournamentTournamentIdAttachmentAttachmentId', tournamentId, attachmentId],
  queryFn: async () => {
    const res = (await getResponse('getApiV1BeachTournamentTournamentIdAttachmentAttachmentId', tournamentId, attachmentId)) as unknown as Response;
    const blob = await res.blob();
    if (blob.type.includes('image')) {
      return URL.createObjectURL(blob);
    }
    return await blob.text();
  },
});

export const getApiV1ClubAbv = (params: GetApiV1ClubAbvParams) => queryOptions({
  queryKey: ['getApiV1ClubAbv', params],
  queryFn: () => getResponse('getApiV1ClubAbv', params),
});

export const getApiV1BeachStatisticsRefereeDelegations = (params?: GetApiV1BeachStatisticsRefereeDelegationsParams) => queryOptions({
  queryKey: ['getApiV1BeachStatisticsRefereeDelegations', params],
  queryFn: () => getResponse('getApiV1BeachStatisticsRefereeDelegations', params ? queryParams(params) : undefined),
});

export const getApiV1BeachStatisticsRefereeEvaluation = (params?: GetApiV1BeachStatisticsRefereeEvaluationParams) => queryOptions({
  queryKey: ['getApiV1BeachStatisticsRefereeEvaluation', params],
  queryFn: () => getResponse('getApiV1BeachStatisticsRefereeEvaluation', params ? queryParams(params) : undefined),
});

export const getApiV1BeachStatisticsRefereeUnavailability = (params?: GetApiV1BeachStatisticsRefereeUnavailabilityParams) => queryOptions({
  queryKey: ['getApiV1BeachStatisticsRefereeUnavailability', params],
  queryFn: () => getResponse('getApiV1BeachStatisticsRefereeUnavailability', params ? queryParams(params) : undefined),
});

export const getApiV1BeachTournamentGetEvaluationTournamentId = (tournamentId: number) => queryOptions({
  queryKey: ['getApiV1BeachTournamentGetEvaluationTournamentId', tournamentId],
  queryFn: () => getResponse('getApiV1BeachTournamentGetEvaluationTournamentId', tournamentId),
});

export const getApiV1BeachApplicationTournamentId = (tournamentId: number, params?: BaseTableParams) => queryOptions({
  queryKey: ['getApiV1BeachApplicationTournamentId', tournamentId, params],
  queryFn: () => getResponse('getApiV1BeachApplicationTournamentId', tournamentId, params),
});

export const getApiV1BeachApplicationTournamentIdQualification = (tournamentId: number) => queryOptions({
  queryKey: ['getApiV1BeachApplicationTournamentIdQualification', tournamentId],
  queryFn: () => getResponse('getApiV1BeachApplicationTournamentIdQualification', tournamentId),
});

export const getApiV1BeachApplicationTournamentIdMainCompetition = (tournamentId: number) => queryOptions({
  queryKey: ['getApiV1BeachApplicationTournamentIdMainCompetition', tournamentId],
  queryFn: () => getResponse('getApiV1BeachApplicationTournamentIdMainCompetition', tournamentId),
});

export const getApiV1BeachFolderFolderId = (folderId: number) => queryOptions({
  queryKey: ['getApiV1BeachFolderFolderId', folderId],
  queryFn: () => getResponse('getApiV1BeachFolderFolderId', folderId),
});

export const getApiV1BeachRefereePaymentsTournaments = (params: BaseTableParams<GetApiV1BeachRefereePaymentsTournamentsParams>) => queryOptions({
  queryKey: ['getApiV1BeachRefereePaymentsTournaments', params],
  queryFn: () => getResponse('getApiV1BeachRefereePaymentsTournaments', paginationToApiFormat(params, true)),
});

export const getApiV1BeachRefereePaymentsTournamentIdPaymentUserId = (tournamentId: number, userId: number) => queryOptions({
  queryKey: ['getApiV1BeachRefereePaymentsTournamentIdPaymentUserId', tournamentId, userId],
  queryFn: () => getResponse('getApiV1BeachRefereePaymentsTournamentIdPaymentUserId', tournamentId, userId),
});

export const getApiV1BeachRefereePaymentsTournamentIdReferees = (tournamentId: number, params: BaseTableParams) => queryOptions({
  queryKey: ['getApiV1BeachRefereePaymentsTournamentIdReferees', tournamentId, params],
  queryFn: () => getResponse('getApiV1BeachRefereePaymentsTournamentIdReferees', tournamentId, paginationToApiFormat(params)),
});

export const getApiV1BeachAllowedFolders = queryOptions({
  queryKey: ['getApiV1BeachAllowedFolders'],
  queryFn: () => getResponse('getApiV1BeachAllowedFolders'),
});
